import React, { useState, useEffect } from 'react';
import { TimelineEventItem } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import {
  Clock,
  RefreshCw,
  Package,
  Truck,
  CheckCircle2,
  AlertTriangle,
  Database,
  Trash2,
  Scale,
  Search,
  ChevronDown,
  ChevronUp,
  Flame,
  Coffee,
} from 'lucide-react';
import { formatNumber, formatQty } from '../utils/format';

type FilterType = 'ALL' | 'SHIFT_CLOSE' | 'PURCHASE_IN' | 'TRANSFER_STOCK' | 'WASTE_LOG' | 'BALANCE_ADJUST' | 'DEV_ALERT';

const PAGE_SIZE = 20;

export const TimelineScreen: React.FC = () => {
  const [events, setEvents] = useState<TimelineEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Record<string | number, boolean>>({});

  // Debounce search query 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadTimeline = async (search: string, type: FilterType) => {
    setLoading(true);
    try {
      const res = await api.getTimeline(PAGE_SIZE, undefined, search, type);
      const items = res.data || [];
      setEvents(items);
      setHasMore(!!res.has_more);
      setNextCursor(res.next_cursor ?? null);
    } catch (err: any) {
      tg.alert(`Không tải được timeline: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTimeline = async () => {
    if (loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await api.getTimeline(PAGE_SIZE, nextCursor, debouncedSearch, filterType);
      const newItems = res.data || [];
      setEvents((prev) => [...prev, ...newItems]);
      setHasMore(!!res.has_more);
      setNextCursor(res.next_cursor ?? null);
    } catch (err: any) {
      tg.alert(`Lỗi tải thêm lịch sử: ${err.message}`);
    } finally {
      setLoadingMore(false);
    }
  };

  // Re-fetch whenever debouncedSearch or filterType changes
  useEffect(() => {
    loadTimeline(debouncedSearch, filterType);
  }, [debouncedSearch, filterType]);

  const toggleExpand = (id: string | number) => {
    tg.haptic.selectionChanged();
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getEventBadge = (type: string) => {
    switch (type) {
      case 'PURCHASE_IN':
        return { label: 'L1: Nhập Kho', bg: '#EAF6EE', color: '#1E7B48', icon: <Package size={15} /> };
      case 'TRANSFER_STOCK':
        return { label: 'L2: Chuyển Kho', bg: '#FEF3C7', color: '#D97706', icon: <Truck size={15} /> };
      case 'SHIFT_CLOSE':
        return { label: 'L3: Chốt Ca', bg: '#FEE2E2', color: '#DC2626', icon: <CheckCircle2 size={15} /> };
      case 'WASTE_LOG':
        return { label: 'L4: Hủy Hỏng', bg: '#FEE2E2', color: '#EF4444', icon: <Trash2 size={15} /> };
      case 'BALANCE_ADJUST':
        return { label: 'L5: Cân Bằng', bg: '#E0E7FF', color: '#4F46E5', icon: <Scale size={15} /> };
      case 'DEV_ALERT':
        return { label: 'Cảnh Báo Dev', bg: '#FEF2F2', color: '#DC2626', icon: <AlertTriangle size={15} /> };
      case 'SYNC_MASTER':
        return { label: 'Master Data', bg: '#EFF6FF', color: '#2563EB', icon: <Database size={15} /> };
      default:
        return { label: 'Hệ Thống', bg: 'var(--bg-surface-subtle)', color: 'var(--text-tertiary)', icon: <Clock size={15} /> };
    }
  };

  const renderSingleAlert = (alt: string, idx: number) => {
    const isLoss = alt.includes('Hao hụt bất thường');
    const isLow = alt.includes('tồn kho thấp');
    const isUncounted = alt.includes('Chưa kiểm kê');

    // Regex bóc tách
    const nameMatch = alt.match(/"([^"]+)"/);
    const itemName = nameMatch ? nameMatch[1] : '';

    if (isLoss && itemName) {
      const actualMatch = alt.match(/Thực tế dùng ([^,]+)/);
      const theoryMatch = alt.match(/lý thuyết ([^(\n]+)/);
      const diffMatch = alt.match(/Chênh lệch ([^,\n]+)/);
      const pctMatch = alt.match(/tức ([^,\n]+)/);
      const toleranceMatch = alt.match(/ngưỡng cho phép ([^)\n]+)/);

      return (
        <div
          key={idx}
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #FCA5A5',
            borderLeft: '4px solid #DC2626',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#991B1B' }}>
              ⚠️ {itemName}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                backgroundColor: '#FEE2E2',
                color: '#B91C1C',
                padding: '1px 6px',
                borderRadius: 4,
              }}
            >
              Lệch: {diffMatch ? diffMatch[1] : ''} ({pctMatch ? pctMatch[1] : ''})
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#4B5563', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>Thực tế dùng: <strong style={{ color: '#111827' }}>{actualMatch ? actualMatch[1] : '-'}</strong></span>
            <span>•</span>
            <span>Định mức bán: <strong style={{ color: '#111827' }}>{theoryMatch ? theoryMatch[1] : '-'}</strong></span>
            {toleranceMatch && (
              <>
                <span>•</span>
                <span style={{ color: '#6B7280' }}>(Ngưỡng: {toleranceMatch[1]})</span>
              </>
            )}
          </div>
        </div>
      );
    }

    if (isLow && itemName) {
      const remainMatch = alt.match(/Còn ([^(\n]+)/);
      const safeMatch = alt.match(/tối thiểu ([^)\n]+)/);
      return (
        <div
          key={idx}
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #FDBA74',
            borderLeft: '4px solid #EA580C',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#9A3412' }}>
              📉 Tồn kho thấp: {itemName}
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#C2410C', backgroundColor: '#FFEDD5', padding: '1px 6px', borderRadius: 4 }}>
              Cần châm kho
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#4B5563' }}>
            Hiện còn: <strong style={{ color: '#EA580C' }}>{remainMatch ? remainMatch[1] : '-'}</strong> (Mức an toàn: {safeMatch ? safeMatch[1] : '-'})
          </div>
        </div>
      );
    }

    if (isUncounted && itemName) {
      return (
        <div
          key={idx}
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #CBD5E1',
            borderLeft: '4px solid #64748B',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 10px',
            fontSize: 11.5,
            color: '#475569',
          }}
        >
          ℹ️ <strong>{itemName}</strong>: Chưa nhập số cân đo thực tế cuối ca
        </div>
      );
    }

    // Fallback cho câu thông thường
    return (
      <div
        key={idx}
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #FECACA',
          borderRadius: 'var(--radius-sm)',
          padding: '6px 10px',
          fontSize: 11.5,
          color: '#B91C1C',
          lineHeight: 1.4,
        }}
      >
        {alt}
      </div>
    );
  };

  const getRelativeTime = (dateString: string) => {
    const normalizedDateString = dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z';
    const date = new Date(normalizedDateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const rtf = new Intl.RelativeTimeFormat('vi-VN', { numeric: 'auto' });

    if (diffInSeconds < 60) return 'Vừa xong';
    if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');

    return date.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>Dòng Sự Kiện & Tiêu Thụ</h2>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Lịch sử giao dịch & Nguyên liệu tiêu thụ chi tiết từng ca làm
          </span>
        </div>
        <button
          onClick={() => { tg.haptic.impact('light'); loadTimeline(debouncedSearch, filterType); }}
          className="stepper-btn"
          title="Làm mới"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            backgroundColor: 'var(--bg-surface)',
            border: '1.5px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            padding: '6px 12px',
          }}
        >
          <Search size={16} color="var(--text-tertiary)" />
          <input
            type="text"
            placeholder="Tìm theo nguyên liệu, mã phiếu, ca làm..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13,
              color: 'var(--text-primary)',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--text-tertiary)' }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Quick Filter Pills */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            paddingBottom: 2,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {[
            { id: 'ALL', label: 'Tất cả' },
            { id: 'SHIFT_CLOSE', label: 'Chốt ca (L3)' },
            { id: 'DEV_ALERT', label: 'Cảnh báo Dev' },
            { id: 'PURCHASE_IN', label: 'Nhập hàng (L1)' },
            { id: 'TRANSFER_STOCK', label: 'Chuyển kho (L2)' },
            { id: 'WASTE_LOG', label: 'Hủy hỏng (L4)' },
            { id: 'BALANCE_ADJUST', label: 'Cân bằng (L5)' },
          ].map((item) => {
            const isSelected = filterType === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  tg.haptic.selectionChanged();
                  setFilterType(item.id as FilterType);
                }}
                style={{
                  padding: '5px 11px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  border: isSelected ? '1px solid var(--primary-accent)' : '1px solid var(--border-color)',
                  backgroundColor: isSelected ? 'var(--primary-accent)' : 'var(--bg-surface)',
                  color: isSelected ? '#FFFFFF' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 36, color: 'var(--text-tertiary)' }}>
          <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
          Đang tải lịch sử timeline...
        </div>
      ) : events.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-tertiary)' }}>
          Không tìm thấy sự kiện nào phù hợp.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {events.map((ev, index) => {
            const normalizedDateString = ev.created_at.includes('T') ? ev.created_at : ev.created_at.replace(' ', 'T') + 'Z';
            const timeFormatted = new Date(normalizedDateString).toLocaleString('vi-VN', {
              timeZone: 'Asia/Ho_Chi_Minh',
              hour: '2-digit',
              minute: '2-digit',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
            const relativeTime = getRelativeTime(ev.created_at);
            const badge = getEventBadge(ev.event_type);
            const isShiftClose = ev.event_type === 'SHIFT_CLOSE';
            const isDevAlert = ev.event_type === 'DEV_ALERT';
            const isPurchaseIn = ev.event_type === 'PURCHASE_IN';
            const isTransfer = ev.event_type === 'TRANSFER_STOCK';
            const isWaste = ev.event_type === 'WASTE_LOG';
            const isBalance = ev.event_type === 'BALANCE_ADJUST';
            const meta = ev.metadata || {};
            const rawConsumptions = meta.raw_consumptions || [];
            const salesItems: Array<{ product_name: string; quantity: number }> = meta.sales_items || [];
            const alerts: string[] = meta.alerts || [];
            const isExpanded = expandedIds[ev.id || index] ?? (isShiftClose && index === 0);

            // Tự động nhận diện nếu description chứa các dòng cảnh báo dạng "⚠️ [Hao hụt bất thường]..."
            const descLines = ev.description ? ev.description.split('\n').filter((l) => l.trim().length > 0) : [];
            const hasAlertFormat = descLines.some((l) => l.includes('Hao hụt bất thường') || l.includes('tồn kho thấp') || l.includes('Chưa kiểm kê'));

            return (
              <div
                key={ev.id || index}
                className="card"
                style={{
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  border: isShiftClose
                    ? '1.5px solid rgba(220, 38, 38, 0.25)'
                    : isDevAlert
                    ? '1.5px solid rgba(220, 38, 38, 0.4)'
                    : '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-surface)',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                {/* Event Card Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: badge.bg,
                        color: badge.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {badge.icon}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: badge.bg,
                            color: badge.color,
                            textTransform: 'uppercase',
                          }}
                        >
                          {badge.label}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>
                          {ev.title}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                        {timeFormatted} • <span style={{ color: 'var(--primary-accent)', fontWeight: 600 }}>{relativeTime}</span>
                        {meta.shift_id && (
                          <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 4, backgroundColor: 'var(--bg-surface-subtle)', fontWeight: 700, color: 'var(--text-secondary)' }}>
                            #{meta.shift_id}
                          </span>
                        )}
                        {meta.reference_id && (
                          <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 4, backgroundColor: 'var(--bg-surface-subtle)', fontWeight: 700, color: 'var(--text-secondary)' }}>
                            #{meta.reference_id}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Description (Styled clear & readable) - Ẩn bớt text thô lặp lại khi đã có khối thẻ chi tiết (L1->L5) */}
                {ev.description && (!isShiftClose || alerts.length === 0) && (
                  hasAlertFormat ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
                      {descLines.map((line, lIdx) => renderSingleAlert(line, lIdx))}
                    </div>
                  ) : (!meta.items || !Array.isArray(meta.items) || meta.items.length === 0) ? (
                    <div
                      style={{
                        fontSize: 12.5,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: 'var(--bg-surface-subtle)',
                        borderLeft: `3px solid ${badge.color}`,
                      }}
                    >
                      {ev.description}
                    </div>
                  ) : null
                )}

                {/* ========================================================================= */}
                {/* L3 CHỐT CA: HORIZONTAL DETAILS (MÓN ĐÃ BÁN, CẢNH BÁO, TIÊU THỤ) */}
                {/* ========================================================================= */}
                {isShiftClose && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
                    {/* Horizontal KPI Badges */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 100,
                          backgroundColor: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          borderRadius: 'var(--radius-md)',
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Coffee size={16} color="#16A34A" />
                        <div>
                          <div style={{ fontSize: 10, color: '#15803D', fontWeight: 700 }}>MÓN ĐÃ BÁN</div>
                          <div style={{ fontSize: 13, fontWeight: 900, color: '#166534' }}>
                            {meta.sales_count || 0} ly/món
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minWidth: 100,
                          backgroundColor: meta.alerts_count > 0 ? '#FEF2F2' : '#F8FAFC',
                          border: `1px solid ${meta.alerts_count > 0 ? '#FECACA' : '#E2E8F0'}`,
                          borderRadius: 'var(--radius-md)',
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <AlertTriangle size={16} color={meta.alerts_count > 0 ? '#DC2626' : '#64748B'} />
                        <div>
                          <div style={{ fontSize: 10, color: meta.alerts_count > 0 ? '#B91C1C' : '#64748B', fontWeight: 700 }}>
                            CẢNH BÁO KHO
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 900, color: meta.alerts_count > 0 ? '#991B1B' : '#475569' }}>
                            {meta.alerts_count || 0} mục
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minWidth: 100,
                          backgroundColor: '#FFF7ED',
                          border: '1px solid #FED7AA',
                          borderRadius: 'var(--radius-md)',
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Flame size={16} color="#EA580C" />
                        <div>
                          <div style={{ fontSize: 10, color: '#C2410C', fontWeight: 700 }}>TIÊU THỤ</div>
                          <div style={{ fontSize: 13, fontWeight: 900, color: '#9A3412' }}>
                            {rawConsumptions.length} nguyên liệu
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Món đã bán - Triển khai ngang (Chips / Tags) */}
                    {salesItems.length > 0 && (
                      <div
                        style={{
                          backgroundColor: '#FAFAF9',
                          border: '1px solid #E7E5E4',
                          borderRadius: 'var(--radius-md)',
                          padding: '8px 10px',
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#44403C', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Coffee size={13} color="#78716C" />
                          <span>Chi tiết món bán trong ca:</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {salesItems.map((item, idx) => (
                            <span
                              key={idx}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '4px 8px',
                                borderRadius: 'var(--radius-sm)',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #D6D3D1',
                                fontSize: 12,
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                              }}
                            >
                              <span>{item.product_name}</span>
                              <span
                                style={{
                                  backgroundColor: '#E0E7FF',
                                  color: '#3730A3',
                                  padding: '1px 5px',
                                  borderRadius: 4,
                                  fontSize: 11,
                                  fontWeight: 800,
                                }}
                              >
                                {item.quantity} ly
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Danh sách Cảnh báo chi tiết (nếu có) - Render dạng Structured Card */}
                    {alerts.length > 0 && (
                      <div
                        style={{
                          backgroundColor: '#FEF2F2',
                          border: '1px solid #FECACA',
                          borderRadius: 'var(--radius-md)',
                          padding: '10px 12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <AlertTriangle size={14} color="#DC2626" />
                          <span>Lưu ý & Cảnh báo hao hụt / định mức ({alerts.length}):</span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {alerts.map((alt, idx) => renderSingleAlert(alt, idx))}
                        </div>
                      </div>
                    )}

                    {/* Bảng Nguyên Liệu Tiêu Thụ Trong Ca (Collapsible) */}
                    <div
                      style={{
                        border: '1px solid #FEE2E2',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: '#FFFDFD',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        onClick={() => toggleExpand(ev.id || index)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#FEF2F2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Flame size={15} color="#DC2626" />
                          <span style={{ fontSize: 12, fontWeight: 800, color: '#991B1B' }}>
                            Nguyên Liệu Tiêu Thụ Trong Ca {rawConsumptions.length > 0 ? `(${rawConsumptions.length} loại)` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#991B1B', fontSize: 11, fontWeight: 700 }}>
                          <span>{isExpanded ? 'Thu gọn' : 'Xem chi tiết'}</span>
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </div>

                      {/* Detailed Consumption Table */}
                      {isExpanded && (
                        <div style={{ padding: '10px 12px' }}>
                          {rawConsumptions.length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', padding: '6px 0' }}>
                              Không có dữ liệu nguyên liệu tiêu thụ được ghi nhận.
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(0, 1fr) 90px 75px',
                                  gap: 6,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: 'var(--text-tertiary)',
                                  borderBottom: '1px solid #FEE2E2',
                                  paddingBottom: 4,
                                }}
                              >
                                <span>TÊN NGUYÊN LIỆU</span>
                                <span style={{ textAlign: 'right' }}>TIÊU THỤ</span>
                                <span style={{ textAlign: 'right' }}>ĐỊNH MỨC</span>
                              </div>

                              {rawConsumptions.map((rc: any, idx: number) => (
                                <div
                                  key={idx}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) 90px 75px',
                                    gap: 6,
                                    alignItems: 'center',
                                    fontSize: 12,
                                    padding: '5px 0',
                                    borderBottom: idx < rawConsumptions.length - 1 ? '1px dashed #FEE2E2' : 'none',
                                  }}
                                >
                                  <div style={{ minWidth: 0, fontWeight: 700, color: 'var(--text-primary)' }}>
                                    • {rc.name}
                                    {rc.is_low_stock && (
                                      <span style={{ marginLeft: 4, fontSize: 10, color: '#DC2626', fontWeight: 800 }}>
                                        ⚠️ Sắp hết
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'right', fontWeight: 800, color: '#DC2626' }}>
                                    {formatQty(rc.consumed, rc.unit)}
                                  </div>
                                  <div style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 11 }}>
                                    {formatQty(rc.theoretical, rc.unit)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ========================================================================= */}
                {/* L1 NHẬP KHO: CHI TIẾT CÁC MÓN NHẬP (NGANG) */}
                {/* ========================================================================= */}
                {isPurchaseIn && meta.items && Array.isArray(meta.items) && meta.items.length > 0 && (
                  <div
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #DCFCE7',
                      borderRadius: 'var(--radius-md)',
                      padding: '8px 10px',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#166534', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Package size={13} color="#16A34A" />
                      <span>Danh sách mặt hàng nhập kho tổng:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {meta.items.map((it: any, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #BBF7D0',
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span>{it.ingredient_name || it.name}</span>
                          <span
                            style={{
                              backgroundColor: '#DCFCE7',
                              color: '#15803D',
                              padding: '1px 5px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            +{formatQty(it.quantity || it.standard_qty, it.unit || it.package_unit)}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ========================================================================= */}
                {/* L2 CHUYỂN KHO: CHI TIẾT CÁC MÓN CHUYỂN (NGANG) */}
                {/* ========================================================================= */}
                {isTransfer && meta.items && Array.isArray(meta.items) && meta.items.length > 0 && (
                  <div
                    style={{
                      backgroundColor: '#FFFBEB',
                      border: '1px solid #FEF3C7',
                      borderRadius: 'var(--radius-md)',
                      padding: '8px 10px',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#92400E', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Truck size={13} color="#D97706" />
                      <span>Nguyên liệu xuất kho tổng ➔ Quầy bar:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {meta.items.map((it: any, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #FDE68A',
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span>{it.ingredient_name || it.name}</span>
                          <span
                            style={{
                              backgroundColor: '#FEF3C7',
                              color: '#B45309',
                              padding: '1px 5px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            {formatQty(it.quantity, it.unit)}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ========================================================================= */}
                {/* L4 HỦY HỎNG: CHI TIẾT CÁC MÓN HỦY (NGANG) */}
                {/* ========================================================================= */}
                {isWaste && meta.items && Array.isArray(meta.items) && meta.items.length > 0 && (
                  <div
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FEE2E2',
                      borderRadius: 'var(--radius-md)',
                      padding: '8px 10px',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#991B1B', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={13} color="#EF4444" />
                      <span>Chi tiết các mặt hàng báo hủy:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {meta.items.map((it: any, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #FECACA',
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span>{it.item_name || it.name}</span>
                          <span
                            style={{
                              backgroundColor: '#FEE2E2',
                              color: '#DC2626',
                              padding: '1px 5px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            -{formatQty(it.quantity, it.unit)}
                          </span>
                          {it.reason && (
                            <span style={{ fontSize: 10, color: '#7F1D1D', fontStyle: 'italic' }}>
                              ({it.reason})
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ========================================================================= */}
                {/* L5 CÂN BẰNG TỒN KHO: CHI TIẾT TỪNG MÓN ĐIỀU CHỈNH (NGANG) */}
                {/* ========================================================================= */}
                {isBalance && meta.items && Array.isArray(meta.items) && meta.items.length > 0 && (
                  <div
                    style={{
                      backgroundColor: '#EEF2FF',
                      border: '1px solid #E0E7FF',
                      borderRadius: 'var(--radius-md)',
                      padding: '8px 10px',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#3730A3', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Scale size={13} color="#4F46E5" />
                      <span>Số lượng kiểm kê thực tế ghi nhận:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {meta.items.map((it: any, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #C7D2FE',
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span>{it.name || it.ingredient_name}</span>
                          <span
                            style={{
                              backgroundColor: '#E0E7FF',
                              color: '#4338CA',
                              padding: '1px 5px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            = {formatNumber(it.actual_quantity)}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* INFINITE LOAD TRIGGER / BUTTON */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 10, marginBottom: 20 }}>
              <button
                type="button"
                onClick={loadMoreTimeline}
                disabled={loadingMore}
                className="btn btn-secondary"
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-surface)',
                  border: '1.5px solid var(--border-color)',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                {loadingMore ? (
                  <>
                    <RefreshCw size={15} className="animate-spin" />
                    Đang tải thêm lịch sử cũ hơn...
                  </>
                ) : (
                  <>
                    <Clock size={15} color="var(--primary-accent)" />
                    Tải thêm lịch sử cũ hơn ({PAGE_SIZE} sự kiện tiếp theo)
                  </>
                )}
              </button>
            </div>
          )}

          {!hasMore && events.length > 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 11, color: 'var(--text-tertiary)' }}>
              Đã hiển thị toàn bộ lịch sử giao dịch & tiêu thụ.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
